import { useCallback, useMemo } from 'react';
import { Pressable, View, type ViewProps } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import {
  MealsByIdsDocument,
  SearchMealsDocument,
  type MealNutritionalInfoFragment,
} from '~/graphql/generated/graphql';
import { Feature } from '@fytness/shared';
import { Icon } from '~/global/components/icon/Icon';
import { useModal } from '@/contexts/ModalContext';
import { useFeatureGuard } from '~/purchases/hooks/useFeatureGuard';
import { MealFormDialog } from '../meal-form-dialog/MealFormDialog';

const CUSTOM_MEALS_LIMIT = 20;

interface CustomMealView {
  id: string;
  name: string;
  secondary: string;
  calories: number;
  macros: string;
}

interface CustomMealItemProps {
  meal: CustomMealView;
  isLast: boolean;
  caloriesUnit: string;
  onPress: () => void;
}

const CustomMealItem = ({
  meal,
  isLast,
  caloriesUnit,
  onPress,
}: CustomMealItemProps) => (
  <Pressable
    testID="custom-meal-item"
    onPress={onPress}
    className={`flex flex-row items-center gap-3.5 p-4 active:opacity-70 ${!isLast ? 'border-b border-border' : ''}`}
  >
    <View className="h-11 w-11 rounded-lg bg-surface-elevated items-center justify-center">
      <Icon name="Salad" size={20} className="text-accent" />
    </View>
    <View className="flex-1 gap-0.5">
      <Text className="font-inter-medium text-sm text-text-primary">
        {meal.name}
      </Text>
      {!!meal.secondary && (
        <Text className="font-inter-regular text-xs text-text-muted">
          {meal.secondary}
        </Text>
      )}
    </View>
    <View className="items-end gap-0.5">
      <Text className="font-sora-bold text-sm text-text-primary">
        {meal.calories} {caloriesUnit}
      </Text>
      <Text className="font-inter-medium text-[10px] text-text-muted">
        {meal.macros}
      </Text>
    </View>
    <Icon name="ChevronRight" size={16} className="text-text-muted" />
  </Pressable>
);

export interface CustomMealsSectionProps extends ViewProps {}

export const CustomMealsSection = ({
  className,
  ...props
}: CustomMealsSectionProps) => {
  const { t } = useTranslation('meal');
  const { showModal } = useModal();
  const guard = useFeatureGuard();

  const { data: searchData, refetch } = useQuery(SearchMealsDocument, {
    variables: { query: { onlyMine: true, limit: CUSTOM_MEALS_LIMIT } },
  });

  const items = useMemo(() => searchData?.meals?.items ?? [], [searchData]);

  const mealIds = useMemo(
    () => Array.from(new Set(items.map((m) => m.id))),
    [items],
  );

  const { data: mealsData } = useQuery(MealsByIdsDocument, {
    variables: { ids: mealIds },
    skip: mealIds.length === 0,
  });

  const nutritionById = useMemo(() => {
    const map = new Map<string, MealNutritionalInfoFragment>();
    (mealsData?.mealsByIds ?? []).forEach((m) => {
      if (m.nutritionalInfo) map.set(m.id, m.nutritionalInfo);
    });
    return map;
  }, [mealsData]);

  const meals = useMemo((): CustomMealView[] => {
    return items.map((meal) => {
      const nutrition = nutritionById.get(meal.id);
      const secondary =
        meal.brand ??
        `${meal.servingSize} ${t(`units.${meal.servingUnit}.shortSign`)}`;
      return {
        id: meal.id,
        name: meal.name,
        secondary,
        calories: Math.round(nutrition?.energyKcal ?? 0),
        macros: t('macroShort', {
          carbs: Math.round(nutrition?.carbs ?? 0),
          protein: Math.round(nutrition?.protein ?? 0),
          fats: Math.round(nutrition?.fats ?? 0),
        }),
      };
    });
  }, [items, nutritionById, t]);

  const handleAdd = useCallback(() => {
    // Creating a custom food is a Pro feature — non-entitled users get the
    // paywall instead of the form.
    guard(Feature.CUSTOM_FOODS, () => {
      showModal(MealFormDialog, {
        onMealSaved: () => {
          refetch();
        },
      });
    });
  }, [guard, showModal, refetch]);

  const handleEdit = useCallback(
    (mealId: string) => {
      showModal(MealFormDialog, {
        mealId,
        onMealSaved: () => {
          refetch();
        },
      });
    },
    [showModal, refetch],
  );

  const isEmpty = meals.length === 0;

  return (
    <View className={className} {...props}>
      <View className="flex flex-row items-center justify-between mb-3">
        <View className="flex flex-row items-center gap-3">
          <View className="h-0.5 w-6 rounded-sm bg-accent" />
          <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
            {t('customMeals.title')}
          </Text>
        </View>
        <Pressable
          testID="custom-meal-add"
          onPress={handleAdd}
          className="flex flex-row items-center gap-1.5"
        >
          <Icon name="Plus" size={14} className="text-accent" />
          <Text className="font-inter-medium text-xs text-accent">
            {t('customMeals.add')}
          </Text>
        </Pressable>
      </View>

      <View className="rounded-xl bg-surface overflow-hidden">
        {isEmpty ? (
          <View className="py-8">
            <Text className="font-inter-regular text-text-muted text-center text-sm">
              {t('customMeals.empty')}
            </Text>
          </View>
        ) : (
          meals.map((meal, index) => (
            <CustomMealItem
              key={meal.id}
              meal={meal}
              isLast={index === meals.length - 1}
              caloriesUnit={t('nutrition.kcal.unit')}
              onPress={() => handleEdit(meal.id)}
            />
          ))
        )}
      </View>
    </View>
  );
};
